import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const AILoader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("loading"); // loading → complete

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setPhase("complete");
          setTimeout(onComplete, 600);
          return 100;
        }
        return prev + Math.random() * 8 + 2;
      });
    }, 80);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {phase !== "hidden" && (
        <motion.div
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0F172A 0%, #0c1529 50%, #0F172A 100%)" }}
        >
          {/* Background grid */}
          <div className="absolute inset-0 tech-grid opacity-30" />

          {/* Gradient orbs */}
          <div
            className="absolute w-[400px] h-[400px] rounded-full opacity-20 blur-[100px]"
            style={{
              background: "radial-gradient(circle, #6366F1, transparent 70%)",
              animation: "float 6s ease-in-out infinite",
            }}
          />
          <div
            className="absolute w-[300px] h-[300px] rounded-full opacity-15 blur-[80px]"
            style={{
              top: "60%",
              left: "60%",
              background: "radial-gradient(circle, #22D3EE, transparent 70%)",
              animation: "float 5s ease-in-out infinite reverse",
            }}
          />

          {/* Neural brain animation */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative mb-10"
          >
            {/* Outer pulsing rings */}
            {[100, 140, 180].map((size, i) => (
              <motion.div
                key={i}
                animate={{ scale: [1, 1.15, 1], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 2 + i * 0.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                className="absolute rounded-full"
                style={{
                  width: size,
                  height: size,
                  top: "50%",
                  left: "50%",
                  marginLeft: -size / 2,
                  marginTop: -size / 2,
                  border: `1px solid rgba(99, 102, 241, ${0.3 - i * 0.08})`,
                }}
              />
            ))}

            {/* Core brain circle */}
            <motion.div
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="relative w-24 h-24 rounded-full flex items-center justify-center"
              style={{
                background: "radial-gradient(circle, rgba(99, 102, 241, 0.3), rgba(34, 211, 238, 0.1))",
                boxShadow: "0 0 60px rgba(99, 102, 241, 0.4), 0 0 120px rgba(34, 211, 238, 0.15)",
                border: "1px solid rgba(99, 102, 241, 0.3)",
              }}
            >
              {/* Brain SVG */}
              <svg
                viewBox="0 0 24 24"
                className="w-10 h-10"
                fill="none"
                stroke="#22D3EE"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ filter: "drop-shadow(0 0 10px rgba(34, 211, 238, 0.6))" }}
              >
                <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
                <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z" />
                <path d="M15 13a4.5 4.5 0 0 1-3-4 4.5 4.5 0 0 1-3 4" />
                <path d="M17.599 6.5a3 3 0 0 0 .399-1.375" />
                <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
                <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
                <path d="M19.938 10.5a4 4 0 0 1 .585.396" />
                <path d="M6 18a4 4 0 0 1-1.967-.516" />
                <path d="M19.967 17.484A4 4 0 0 1 18 18" />
              </svg>

              {/* Rotating data particles around brain */}
              {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                <motion.div
                  key={i}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 6 + i, repeat: Infinity, ease: "linear", delay: i * 0.4 }}
                  className="absolute w-0 h-0"
                  style={{ top: "50%", left: "50%" }}
                >
                  <div
                    className="absolute rounded-full"
                    style={{
                      width: 4,
                      height: 4,
                      transform: `translateX(${50 + i * 4}px)`,
                      background: i % 2 === 0 ? "#6366F1" : "#22D3EE",
                      boxShadow: `0 0 8px ${i % 2 === 0 ? "#6366F1" : "#22D3EE"}`,
                    }}
                  />
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Brand text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl font-bold text-white mb-2 tracking-wide">
              Client<span style={{ color: "#22D3EE" }}>Code</span> AI
            </h2>
            <p className="text-sm text-slate-400 tracking-widest uppercase">
              Initializing Neural Systems
            </p>
          </motion.div>

          {/* Progress bar */}
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 240 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="relative h-1 rounded-full overflow-hidden"
            style={{ background: "rgba(99, 102, 241, 0.15)" }}
          >
            <motion.div
              className="absolute inset-y-0 left-0 rounded-full"
              style={{
                width: `${Math.min(progress, 100)}%`,
                background: "linear-gradient(90deg, #6366F1, #22D3EE)",
                boxShadow: "0 0 12px rgba(34, 211, 238, 0.5)",
                transition: "width 0.15s ease",
              }}
            />
          </motion.div>

          {/* Progress text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-xs text-slate-500 mt-3 font-mono"
          >
            {Math.min(Math.round(progress), 100)}%
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AILoader;
